import { useState } from 'react'
import { useAnnouncements, useAnnouncementMutations } from './hooks/useAnnouncements'
import { useAuth } from '../../hooks/useAuth'
import { cn } from '../../lib/utils'
import { memberColors } from '../../styles/tokens'
import { Portal } from '../../components/Portal'

// ── Markdown renderer (subset: bold, italic, paragraphs) ──────────────────
const renderBody = (text) => {
  return text
    .split('\n\n')
    .filter(Boolean)
    .map((para, i) => {
      const html = para
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br/>')
      return (
        <p key={i} className="text-text-secondary text-sm leading-relaxed"
          dangerouslySetInnerHTML={{ __html: html }} />
      )
    })
}

// ── Announcement Modal ─────────────────────────────────────────────────────
const AnnouncementModal = ({ initial, onClose, onSave }) => {
  const [title, setTitle] = useState(initial?.title ?? '')
  const [body, setBody] = useState(initial?.body ?? '')
  const [isPinned, setIsPinned] = useState(initial?.is_pinned ?? false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    if (!title.trim()) return setError('Title is required')
    if (!body.trim()) return setError('Body is required')
    setSaving(true)
    try {
      await onSave({ title: title.trim(), body: body.trim(), is_pinned: isPinned })
      onClose()
    } catch (e) {
      setError(e.message ?? 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Portal>
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-4 overflow-hidden">
      <div className="bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90dvh] overflow-y-auto">
        <h2 className="font-bold text-text-primary text-lg">
          {initial ? 'Edit announcement' : 'New announcement'}
        </h2>

        {error && <p className="text-red-500 text-sm">{error}</p>}

        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />

        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write your announcement… Supports **bold** and *italic*."
          rows={6}
          className="w-full border border-border rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
        />

        <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={isPinned}
            onChange={(e) => setIsPinned(e.target.checked)}
            className="rounded border-border text-primary"
          />
          Pin to top
        </label>

        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Post'}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  )
}

// ── Card ────────────────────────────────────────────────────────────────────
const AnnouncementCard = ({ item, isAdmin, onEdit, onDelete, onTogglePin }) => {
  const color = memberColors[item.profiles?.member_color] ?? '#7c6faf'
  const initials = (item.profiles?.display_name ?? '?').slice(0, 2).toUpperCase()
  const date = new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  return (
    <div className={cn(
      'bg-surface border rounded-2xl p-5',
      item.is_pinned ? 'border-primary/30 bg-primary/5' : 'border-border'
    )}>
      {item.is_pinned && (
        <div className="flex items-center gap-1.5 mb-3">
          <svg className="w-3.5 h-3.5 text-primary" fill="currentColor" viewBox="0 0 24 24">
            <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
          </svg>
          <span className="text-xs font-semibold text-primary uppercase tracking-wide">Pinned</span>
        </div>
      )}

      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-semibold text-text-primary text-base">{item.title}</h3>
        {isAdmin && (
          <div className="flex gap-1 shrink-0">
            <button
              onClick={() => onTogglePin({ id: item.id, is_pinned: !item.is_pinned })}
              className="p-1.5 text-text-muted hover:text-primary rounded-lg hover:bg-primary/10"
              title={item.is_pinned ? 'Unpin' : 'Pin'}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
              </svg>
            </button>
            <button
              onClick={() => onEdit(item)}
              className="p-1.5 text-text-muted hover:text-text-secondary rounded-lg hover:bg-gray-100"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => onDelete(item.id)}
              className="p-1.5 text-text-muted hover:text-red-500 rounded-lg hover:bg-red-50"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      <div className="space-y-1 mb-4">{renderBody(item.body)}</div>

      <div className="flex items-center gap-2 pt-2 border-t border-border/50">
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
          style={{ backgroundColor: color }}
        >
          {initials}
        </div>
        <span className="text-xs text-text-muted">{item.profiles?.display_name ?? 'Unknown'}</span>
        <span className="text-xs text-text-muted ml-auto">{date}</span>
      </div>
    </div>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────
export const AnnouncementsPage = () => {
  const { isAdmin } = useAuth()
  const { data: announcements = [], isLoading } = useAnnouncements()
  const { create, update, remove, togglePin } = useAnnouncementMutations()
  const [modal, setModal] = useState(null) // null | 'new' | announcement object

  const handleDelete = async (id) => {
    if (!confirm('Delete this announcement?')) return
    await remove.mutateAsync(id)
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 page-enter">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Announcements</h1>
        {isAdmin && (
          <button
            onClick={() => setModal('new')}
            className="bg-primary text-white text-sm rounded-xl px-4 py-2.5 font-semibold flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New
          </button>
        )}
      </div>

      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-surface border border-border rounded-2xl p-5 animate-pulse">
              <div className="h-5 bg-border rounded w-2/3 mb-3" />
              <div className="h-3 bg-border rounded w-full mb-2" />
              <div className="h-3 bg-border rounded w-5/6" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && announcements.length === 0 && (
        <div className="text-center py-16">
          <svg className="w-12 h-12 text-border mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
          <p className="text-text-muted">No announcements yet.</p>
          {isAdmin && (
            <button onClick={() => setModal('new')} className="mt-3 text-primary text-sm underline underline-offset-2">
              Post the first one
            </button>
          )}
        </div>
      )}

      {!isLoading && (
        <div className="space-y-4">
          {announcements.map((item) => (
            <AnnouncementCard
              key={item.id}
              item={item}
              isAdmin={isAdmin}
              onEdit={(a) => setModal(a)}
              onDelete={handleDelete}
              onTogglePin={(args) => togglePin.mutate(args)}
            />
          ))}
        </div>
      )}

      {modal && (
        <AnnouncementModal
          initial={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={(fields) =>
            modal === 'new'
              ? create.mutateAsync(fields)
              : update.mutateAsync({ id: modal.id, ...fields })
          }
        />
      )}
    </div>
  )
}
